import { useEffect, useState } from 'react';
import { ArrowRight, Check, Link2, Search, ShieldCheck, Sparkles } from 'lucide-react';
import type { Candidate, Contact, LinkPreview } from '@/lib/types';
import { api, useResource } from '@/lib/api';
import { Avatar, ChannelIcon, EmptyState, ErrorBanner, Loading, useToast } from './shared';
import { Button } from './ui/button';
import { Modal } from './ui/dialog';
export function LinkIdentityModal({ open, onOpenChange, contact, onChange }: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    contact: Contact;
    onChange: () => void;
}) {
    const [search, setSearch] = useState('');
    const [candidate, setCandidate] = useState<Candidate | null>(null);
    const [preview, setPreview] = useState<LinkPreview | null>(null);
    const [privacy, setPrivacy] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const toast = useToast();
    const candidates = useResource<Candidate[]>(open ? `/api/contacts/${contact.id}/candidates?q=${encodeURIComponent(search)}` : null);
    useEffect(() => {
        if (open) {
            setCandidate(null);
            setPreview(null);
            setSearch('');
            setPrivacy(false);
            setError('');
        }
    }, [open, contact.id]);
    const choose = async (c: Candidate) => {
        setBusy(true);
        setError('');
        try {
            const p = await api<LinkPreview>(`/api/identities/${c.identityId}/preview?targetContactId=${contact.id}`);
            setCandidate(c);
            setPreview(p);
        }
        catch (e) {
            setError((e as Error).message);
        }
        finally {
            setBusy(false);
        }
    };
    const link = async () => {
        if (!preview)
            return;
        setBusy(true);
        setError('');
        try {
            await api('/api/identities/link', { method: 'POST', body: { identityId: preview.identityId, targetContactId: contact.id, expectedLinkId: preview.expectedLinkId, confirmPrivacy: privacy } });
            onChange();
            onOpenChange(false);
            toast('Identity linked. Original message history is unchanged.');
        }
        catch (e) {
            setError((e as Error).message);
        }
        finally {
            setBusy(false);
        }
    };
    const dismiss = async (c: Candidate) => {
        try {
            await api(`/api/contacts/${contact.id}/dismiss-match`, { method: 'POST', body: { otherContactId: c.contactId } });
            onChange();
            toast('Match dismissed. You can still link this identity manually.');
        }
        catch (e) {
            toast((e as Error).message, true);
        }
    };
    return <Modal open={open} onOpenChange={value => {
            if (!busy)
                onOpenChange(value);
        }} title={preview ? 'Review identity link' : 'Link another identity'} description={preview ? 'Combine the view, not the provider conversations.' : `Find another channel identity that belongs to ${contact.display_name}.`}>
  {error && <ErrorBanner message={error}/>}
  {preview && candidate ? <><div className="link-preview-people"><div><ChannelIcon channel={candidate.channel} size={23}/><strong>{preview.sourceName}</strong><span>{preview.address}</span></div><ArrowRight size={21}/><div><Avatar name={contact.display_name} color={contact.color}/><strong>{contact.display_name}</strong><span>Unified contact</span></div></div><div className="notice"><ShieldCheck size={18}/><div><strong>{preview.messageCount} messages will join this timeline.</strong><p>{preview.explanation} You can separate this identity later.</p></div></div>{preview.crmWarning && <div className="warning-notice">These contacts have different CRM relationships. This identity’s communication references will project to the target contact’s relationship. CRM relationship records are not merged.</div>}{preview.privacyWarning && <label className="privacy-confirm"><input type="checkbox" checked={privacy} onChange={e => setPrivacy(e.target.checked)}/><span>I understand this identity is private or ignored, and confirm its history should appear under the target contact’s classification.</span></label>}
   <div className="modal-actions"><Button variant="outline" disabled={busy} onClick={() => { setPreview(null); setCandidate(null); }}>Back</Button><Button disabled={busy || (preview.privacyWarning && !privacy)} onClick={() => void link()}><Link2 size={15}/>{busy ? 'Linking…' : 'Confirm identity link'}</Button></div>
  </> : <><div className="search-field"><Search size={16}/><input aria-label="Search channel identities" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search a name, handle, email, or number…"/></div><div className="candidate-list">{candidates.error ? <ErrorBanner message={candidates.error}/> : !candidates.data ? <Loading label="Finding identities…"/> : !candidates.data.length ? <EmptyState title="No other identities found" description="Try a different name, handle, or email address."/> : candidates.data.map(c => <div key={c.identityId} className="candidate-row"><ChannelIcon channel={c.channel} size={20}/><div><strong>{c.name}</strong><span>{c.address}</span><small>{c.connectionName} · {c.classification}</small>{c.contactName.toLowerCase() === contact.display_name.toLowerCase() && <span className="candidate-match"><Sparkles size={11}/>Same name · review before linking</span>}</div><div className="candidate-actions"><Button variant="outline" size="sm" disabled={busy} onClick={() => void choose(c)}>Preview</Button>{c.contactName.toLowerCase() === contact.display_name.toLowerCase() && <button className="muted-link" onClick={() => void dismiss(c)}>Not the same</button>}</div></div>)}</div><p className="modal-footnote">Matching names are only suggestions. Relay never silently merges people.</p></>}
 </Modal>;
}
